import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export interface Status {
  id: string;
  status_group: string;
  label: string;
  colour: string;
  sort: number;
  is_closed: boolean;
}
export interface Tax {
  id: string;
  name: string;
  rate: number;
  is_default: boolean;
}
export interface Decoration {
  id: string;
  name: string;
  category: string | null;
  setup_price: number;
  price_breaks: { qty: number; price: number }[];
  vendor_id: string | null;
  vendors?: { name: string } | null;
}
export interface Vendor {
  id: string;
  name: string;
  email: string | null;
  is_local: boolean;
}

interface Lookups {
  statuses: Status[];
  taxes: Tax[];
  decorations: Decoration[];
  vendors: Vendor[];
  loaded: boolean;
}

let cache: Lookups | null = null;
let pending: Promise<Lookups> | null = null;

async function load(): Promise<Lookups> {
  const [s, t, d, v] = await Promise.all([
    supabase.from('statuses').select('*').eq('archived', false).order('status_group').order('sort'),
    supabase.from('taxes').select('*').eq('archived', false).order('is_default', { ascending: false }),
    supabase.from('decorations').select('*, vendors(name)').eq('archived', false).order('name'),
    supabase.from('vendors').select('id, name, email, is_local').eq('archived', false).order('name'),
  ]);
  cache = {
    statuses: (s.data as Status[]) ?? [],
    taxes: ((t.data as Tax[]) ?? []).map((x) => ({ ...x, rate: Number(x.rate) })),
    decorations: ((d.data as Decoration[]) ?? []).map((x) => ({ ...x, setup_price: Number(x.setup_price) })),
    vendors: (v.data as Vendor[]) ?? [],
    loaded: true,
  };
  return cache;
}

/** Statuses, taxes, decorations and vendors — loaded once per session. */
export function useLookups(): Lookups {
  const [state, setState] = useState<Lookups>(
    cache ?? { statuses: [], taxes: [], decorations: [], vendors: [], loaded: false },
  );
  useEffect(() => {
    if (cache) return;
    pending ??= load();
    pending.then(setState);
  }, []);
  return state;
}

export const statusesFor = (all: Status[], group: string) => all.filter((s) => s.status_group === group);
